import React, { useMemo, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { IChannel, ISubChannelSummary } from "@Interfaces";
import useChannelProfileImg from "@Hooks/useChannelProfileImg";
import { getChannelCoverUrl } from "@Utils/getChannelCoverUrl";
import { Gradient } from "@Components/ChannelHeader/ChannelHeader.styles";
import { ChannelActions, NotificationActions } from "@Store";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import Autocomplete from "@mui/material/Autocomplete";
import useMobileDropdownInputProps from "@Hooks/useMobileDropdownInputProps";
import { isGuestDataStored } from "@Utils/isGuestDataStored";
import { isDataCollectionRequired } from "@Utils/isDataCollectionRequired";
import { useTheme } from "@mui/material/styles";
import getSubChannelSelectionDialogSx from "./SubChannelSelectionDialog.styles";

type SubChannelOption = ISubChannelSummary & {
    description?: string;
};

type SubChannelSelectionDialogProps = {
    parentChannel: IChannel;
    subChannels?: SubChannelOption[];
};

const SubChannelSelectionContent: React.FC<SubChannelSelectionDialogProps> = ({
    parentChannel,
    subChannels = [],
}) => {
    const theme = useTheme();
    const sx = useMemo(() => getSubChannelSelectionDialogSx(theme), [theme]);
    const dispatch = useDispatch<any>();
    const { t } = useTranslation();
    const [selectedOption, setSelectedOption] =
        useState<SubChannelOption | null>(null);
    const [inputValue, setInputValue] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mobileDropdownInputProps } = useMobileDropdownInputProps();

    const coverUrl = getChannelCoverUrl(parentChannel);
    const parentHue = parentChannel.cover?.hue ?? 210;
    const isParentEventProfilePictureVisible =
        parentChannel.settings?.display_profile_picture ?? true;
    const parentAvatar = useChannelProfileImg(
        isParentEventProfilePictureVisible
            ? parentChannel.profile_image?.public_url
            : undefined,
        isParentEventProfilePictureVisible
            ? parentChannel.profile_image?.fid
            : undefined
    );

    const options = useMemo(() => subChannels ?? [], [subChannels]);

    const getOptionAvatar = (option: SubChannelOption) => {
        if (!isParentEventProfilePictureVisible) {
            return null;
        }
        const profile = option.profile_image;
        if (profile?.public_url && profile?.fid) {
            return `${profile.public_url}/${profile.fid}`;
        }
        return null;
    };

    const handleContinue = async () => {
        if (!selectedOption || isSubmitting) return;

        setIsSubmitting(true);
        const linkId = selectedOption.link_id ?? selectedOption._id;
        try {
            const loadedChannel = (await dispatch(
                ChannelActions.getLinkChannelData(linkId) as any
            )) as IChannel | undefined;

            if (!loadedChannel) {
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: t(
                            "share_channel.selection_error",
                            "We couldn't load this event. Please try again."
                        ),
                    })
                );
                setIsSubmitting(false);
                return;
            }

            const shouldOpenDataCollection =
                loadedChannel.polls?.length &&
                !isGuestDataStored() &&
                isDataCollectionRequired(loadedChannel.settings);

            if (shouldOpenDataCollection) {
                const coverUrlForChannel = getChannelCoverUrl(loadedChannel);
                dispatch(
                    dialogActions.openDialog({
                        id: DialogComponents.DataCollectionDialog,
                        props: {
                            settings: loadedChannel.settings,
                            author: {
                                username: loadedChannel.title,
                                avatar: loadedChannel.profile_image,
                            },
                            coverUrl: coverUrlForChannel,
                        },
                    })
                );
            } else {
                dispatch(dialogActions.hideDialog());
            }

            try {
                const url = new URL(window.location.href);
                url.searchParams.set("ch", linkId);
                window.history.replaceState({}, "", url.toString());
            } catch (error) {
                // ignore history errors (older browsers)
            }
        } catch (error) {
            dispatch(
                NotificationActions.notification({
                    snackbarOpen: true,
                    snackbarType: "error",
                    snackbarMessage: t(
                        "share_channel.selection_error",
                        "We couldn't load this event. Please try again."
                    ),
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderHeroBackground = () => {
        if (coverUrl) {
            return (
                <Box
                    sx={{
                        ...sx.heroOverlay,
                        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(0,0,0,0.65)), url(${coverUrl})`,
                    }}
                />
            );
        }

        return (
            <Gradient
                hue={parentHue}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.95,
                }}
            />
        );
    };

    return (
        <Box sx={sx.root}>
            <Box sx={sx.hero}>
                {renderHeroBackground()}
                <Box sx={sx.heroContent}>
                    {isParentEventProfilePictureVisible ? (
                        <Avatar
                            src={parentAvatar}
                            alt={parentChannel.title}
                            sx={sx.parentAvatar}
                        />
                    ) : null}
                    <Typography sx={sx.heroTitle}>
                        {parentChannel.title}
                    </Typography>
                    {parentChannel.description ? (
                        <Typography sx={sx.heroSubtitle}>
                            {parentChannel.description}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
            <Box sx={sx.content}>
                <Box sx={sx.contentInner}>
                    <Typography sx={sx.heading}>
                        {t(
                            "share_channel.select_event_heading",
                            "Search for the {{eventName}}",
                            { eventName: parentChannel.title }
                        )}
                    </Typography>
                    <Autocomplete<SubChannelOption, false, false, false>
                        sx={sx.autocomplete}
                        value={selectedOption}
                        onChange={(_, value) => {
                            if (!isSubmitting) {
                                setSelectedOption(
                                    value as SubChannelOption | null
                                );
                            }
                        }}
                        inputValue={inputValue}
                        onInputChange={(_, newInput) => setInputValue(newInput)}
                        options={options}
                        autoHighlight
                        openOnFocus
                        getOptionLabel={(option) => option.title ?? ""}
                        renderOption={(props, option) => {
                            const avatarUrl = getOptionAvatar(option);

                            return (
                                <li {...props}>
                                    <Box sx={sx.optionWrapper}>
                                        {avatarUrl ? (
                                            <Avatar
                                                src={avatarUrl}
                                                alt={option.title}
                                                sx={sx.optionAvatar}
                                            />
                                        ) : null}
                                        <Box sx={sx.optionTexts}>
                                            <Typography sx={sx.optionTitle}>
                                                {option.title}
                                            </Typography>
                                            {option.description ? (
                                                <Typography
                                                    sx={sx.optionDescription}
                                                >
                                                    {option.description}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                        <ChevronRightIcon color="disabled" />
                                    </Box>
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t(
                                    "share_channel.search_placeholder",
                                    "Find by name"
                                )}
                                variant="outlined"
                                inputProps={{
                                    ...params.inputProps,
                                    ...mobileDropdownInputProps,
                                }}
                            />
                        )}
                    />
                    <Button
                        color="primary"
                        variant="contained"
                        sx={sx.continueButton}
                        disabled={!selectedOption || isSubmitting}
                        onClick={handleContinue}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={22} color="inherit" />
                        ) : (
                            t("share_channel.continue", "Continue")
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default {
    content: SubChannelSelectionContent,
    actions: () => <></>,
    disableCancelButton: true,
    showActons: false,
    showHeader: false,
    isGuestData: false,
};
